import { useMemo } from "react";
import type { EventType } from "../api/types";
import { EventEditForm } from "../components/EventEditForm";
import { Sheet } from "../components/Sheet";
import { toDateTimeLocal } from "../lib/date";

// Forme minimale exploitée par l'éditeur : satisfaite par TimelineEvent (vue worker)
// comme par DayEvent (vue weekly manager). Évite un cast côté appelant manager.
type EditorEvent = { id: string; event_type: EventType; occurred_at: string };

const EVENT_TYPE_LABELS_FR: Record<EventType, string> = {
  attendance_in: "Entrée",
  attendance_out: "Sortie",
  activity_start: "Début activité",
  activity_end: "Fin activité",
  break_start: "Début pause",
  break_end: "Fin pause",
  location_ping: "Position",
  manual_adjustment: "Ajustement",
};

interface Props {
  dayKey: string; // "YYYY-MM-DD"
  dayLabel: string;
  events: EditorEvent[];
  editable: boolean;
  // Mode manager : édition de la feuille d'un autre worker. Présent → les créations
  // passent par le chemin manager (POST /timeevents). Absent → worker self-service.
  workerUrl?: string;
  onClose: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatHHMM(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Heure par défaut proposée pour une insertion entre deux events (ou aux bornes).
function insertDefault(
  dayKey: string,
  prev: EditorEvent | null,
  next: EditorEvent | null,
): string {
  if (prev && next) {
    const mid =
      (new Date(prev.occurred_at).getTime() +
        new Date(next.occurred_at).getTime()) /
      2;
    return toDateTimeLocal(new Date(mid));
  }
  if (prev) {
    return toDateTimeLocal(
      new Date(new Date(prev.occurred_at).getTime() + 30 * 60000),
    );
  }
  if (next) {
    return toDateTimeLocal(
      new Date(new Date(next.occurred_at).getTime() - 30 * 60000),
    );
  }
  const [y, m, d] = dayKey.split("-").map(Number);
  return toDateTimeLocal(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 9, 0));
}

const ROW_BG = "var(--surface)";

/**
 * TimelineEditorSheet : éditeur worker self-service de la journée.
 * Affiche les events en timeline ; permet de modifier un event, d'insérer un
 * event entre deux autres, ou d'en ajouter un librement. Édition active
 * uniquement tant que la feuille est en brouillon (`editable`).
 */
export function TimelineEditorSheet({
  dayKey,
  dayLabel,
  events,
  editable,
  workerUrl,
  onClose,
}: Props) {
  // workerUrl présent = édition manager d'un autre worker → pas self.
  const self = !workerUrl;
  const sorted = useMemo(
    () =>
      events
        .slice()
        .sort(
          (a, b) =>
            new Date(a.occurred_at).getTime() -
            new Date(b.occurred_at).getTime(),
        ),
    [events],
  );

  function InsertSlot({
    prev,
    next,
  }: {
    prev: EditorEvent | null;
    next: EditorEvent | null;
  }) {
    if (!editable) return null;
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2px 0" }}
      >
        <EventEditForm
          mode="create"
          self={self}
          workerUrl={workerUrl}
          defaultOccurredAt={insertDefault(dayKey, prev, next)}
          ctaLabel="+ Insérer ici"
          ctaVariant="secondary"
        />
      </div>
    );
  }

  return (
    <Sheet
      title={dayLabel}
      onClose={onClose}
      footer={
        editable && (
          <div className="sheet-cta">
            <EventEditForm
              mode="create"
              self={self}
              workerUrl={workerUrl}
              defaultOccurredAt={`${dayKey}T12:00`}
              ctaLabel="Ajouter un événement"
              ctaVariant="primary"
            />
          </div>
        )
      }
    >
      {!editable && (
        <div
          className="sub"
          style={{
            marginBottom: 12,
            padding: "8px 10px",
            borderRadius: 6,
            background: "var(--surface)",
            border: "1px solid var(--border)",
          }}
        >
          Feuille envoyée : édition verrouillée.
        </div>
      )}

      {sorted.length === 0 && (
        <div className="empty-state">
          <div className="sub">Aucun événement ce jour.</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <InsertSlot prev={null} next={sorted[0] ?? null} />
        {sorted.map((ev, i) => (
          <div key={ev.id}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: ROW_BG,
                border: "1px solid var(--border)",
              }}
            >
              <div>
                <span
                  style={{
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatHHMM(ev.occurred_at)}
                </span>
                <span style={{ marginLeft: 10, fontSize: 14 }}>
                  {EVENT_TYPE_LABELS_FR[ev.event_type] ?? ev.event_type}
                </span>
              </div>
              {editable && (
                <EventEditForm
                  mode="correct"
                  eventId={ev.id}
                  defaultOccurredAt={toDateTimeLocal(new Date(ev.occurred_at))}
                  ctaLabel="Modifier"
                  ctaVariant="secondary"
                />
              )}
            </div>
            <InsertSlot prev={ev} next={sorted[i + 1] ?? null} />
          </div>
        ))}
      </div>
    </Sheet>
  );
}
